// shared-lib/cabinet-runs/fridgeColumn.js
//
// The panels a run stands either side of its fridge. A vendor family that
// sells a refrigerator end panel builds its own column — Venus's REP2496, 1½"
// thick and 24" deep, flush with its 24"-deep bridge cabinet, a standard-depth
// fridge standing proud (user decision 2026-10-08). Every other family keeps
// our surround: ½" panels, the column as deep as the fridge's type asks
// (`fridgeColumnDepth`). The fill (how far the neighbours stand off), the
// install (`buildFridgeInstall`) and the filler beside the column all read
// these answers. Pure.

const {
  FRIDGE_SIDE_PANEL_THICKNESS,
} = require('../kitchen-assembly/constants');
const { familyOf } = require('./families');
const {
  fridgeEndPanelOf,
  familySizes,
  snapUpperHeightIn,
} = require('./vendorSizes');
const { columnTopY } = require('./upperBand');

const IN = 0.0254;

/**
 * The shortest over-fridge cabinet a line with no size catalog builds, in
 * inches: the shortest wall cabinet sold (user ruling 2026-10-21). Below it the
 * column has no cabinet — the engine had built down to 2" and `style_kitchen`
 * dropped anything under 6", so a styled 88.75" fridge under a 94.5" line
 * came back from its first edit with a 4.74" sliver over it (Golden West,
 * Temple City / Scan 1).
 */
const MIN_OVER_FRIDGE_CABINET_IN = 12;

/**
 * The deepest fridge, doors and handles included, that stands in a
 * counter-depth column, in inches. Counter-depth and built-in fridges are
 * this shallow — every catalog fridge is a Sub-Zero built-in, 28.3"-30.4"
 * over its handles, and the generator's default is a 30" box.
 */
const COUNTER_DEPTH_FRIDGE_MAX_IN = 31;
/** The column such a fridge stands in, flush with the base cabinets. */
const COUNTER_DEPTH_COLUMN_IN = 24;
/** The column a deeper, standard-depth fridge stands in. */
const STANDARD_DEPTH_COLUMN_IN = 30;

/**
 * The panels either side of a run's fridge. How DEEP the column stands is
 * `fridgeColumnDepth`, which reads the same panel.
 *
 * @returns {{ thickness: number, sku: string|null }} metres
 */
function fridgeColumnPanels(run) {
  const panel = fridgeEndPanelOf(familyOf(run));
  return panel
    ? { thickness: panel.w * IN, sku: panel.sku }
    : { thickness: FRIDGE_SIDE_PANEL_THICKNESS, sku: null };
}

/**
 * Is this fridge bay BUILT IN — the run builds its column round it (the
 * surround, the cabinet over it, the panels its neighbours pack against)?
 * Every fridge bay is, unless the kitchen it came from stood the fridge free
 * (`builtIn: false`: option #1 of a scan whose fridge had nothing hung over
 * it, so processing built it no surround). The install, the pad, the seat and
 * the filler beside it all ask this one question.
 */
function fridgeIsBuiltIn(bay) {
  return bay?.builtIn !== false;
}

/**
 * The line a run's fridge column rises to (plan 2026-10-21, C19): a stacked
 * row's top where the run has one (`upperBand.columnTopY`) — but no higher
 * than the end panel a vendor family builds the column from (Venus's
 * REP2496, 96"), and never below the uppers' own line — else the uppers'
 * line. The fill's install, the tall filler beside the column and the web's
 * dropped fridge all read this one answer.
 *
 * @param {Object} run
 * @param {Object} band the run's upper band (`upperBandFor`)
 * @param {number} floorY
 * @returns {number} world Y
 */
function fridgeColumnLineY(run, band, floorY) {
  const top = columnTopY(band);
  if (!band.stack) return top;
  const panel = fridgeEndPanelOf(familyOf(run));
  if (!panel) return top;
  return Math.max(band.topY, Math.min(top, floorY + panel.h * IN));
}

/**
 * How deep a fridge's column is, in metres — the surround, the cabinet over
 * the fridge and the tall filler beside them. ONE rule for the run engine
 * (`buildFridgeInstall`), the fill (`fridgeColumnSpec`, which the filler
 * beside the column reads) and `style_kitchen` (`adjustFridgeSurroundForGlb`;
 * user ruling 2026-10-22). They had three: the engine built the column at the
 * fridge's own box depth, styling shrank the surround to 24" for a fridge up to
 * 31" and left the filler beside it at 30" (Golden West, Temple City /
 * Scan 1).
 *
 * A line that sells its own fridge end panels stands them at their depth
 * (Venus: 24", a deeper fridge standing proud). Any other line builds by the
 * fridge's type: up to `COUNTER_DEPTH_FRIDGE_MAX_IN` deep, a 24" column flush
 * with the base cabinets, the doors and handles standing proud; deeper, 30".
 */
function fridgeColumnDepth(family, fridgeDepth) {
  const panel = fridgeEndPanelOf(family);
  if (panel) return panel.d * IN;
  return (
    (fridgeDepth <= COUNTER_DEPTH_FRIDGE_MAX_IN * IN + 1e-6
      ? COUNTER_DEPTH_COLUMN_IN
      : STANDARD_DEPTH_COLUMN_IN) * IN
  );
}

/**
 * How tall the cabinet over a fridge is, in inches, given `roomIn` of room
 * between the fridge (and its gap) and the column's top — or null for none.
 * ONE rule for the run engine (`buildFridgeInstall`) and `style_kitchen`
 * (`adjustOverFridgeForGlb`): a line with a size catalog hangs the tallest
 * 24"-deep bridge it sells at the column's width that fits (Venus); any other
 * line fills the room, from `MIN_OVER_FRIDGE_CABINET_IN` up. Either way the
 * cabinet's top is on the column's top.
 */
function overFridgeCabinetHeightIn(family, widthIn, roomIn) {
  if (familySizes(family)) {
    return snapUpperHeightIn(family, 'wall-bridge-24', widthIn, roomIn) ?? null;
  }
  return roomIn >= MIN_OVER_FRIDGE_CABINET_IN - 1e-6 ? roomIn : null;
}

module.exports = {
  fridgeIsBuiltIn,
  fridgeColumnPanels,
  fridgeColumnLineY,
  fridgeColumnDepth,
  overFridgeCabinetHeightIn,
  MIN_OVER_FRIDGE_CABINET_IN,
};
